import Immutable from "immutable";
import cloneDeep from "lodash.clonedeep";

/**
 * Special keys will have a "_" prepended to them when sent to Ada APIs.
 */
const SPECIAL_KEYS = Immutable.Set(["type", "id", "with"]);

function convertKey(
  obj: {[key: string]: unknown}, key: string, type: string, capitalize: boolean
): void {
  let newKey;

  if (type === "underscore") {
    newKey = key.replace(/([A-Z])/g, g => `_${g.toLowerCase()}`);

    // Remove underscore from starting character
    if (newKey[0] === "_") {
      newKey = newKey.substr(1);
    }

    // Add underscore back if key is special
    if (SPECIAL_KEYS.has(key.toLowerCase())) {
      newKey = `_${newKey}`;
    }
  } else if (type === "camel" || type === "space") {
    newKey = key.replace(/_([a-z])/g, (g) => {
      if (type === "camel") {
        return g[1].toUpperCase();
      }

      if (type === "space") {
        return ` ${g[1].toUpperCase()}`;
      }

      return "";
    });
  }

  if (capitalize) {
    newKey = newKey.substr(0, 1).toUpperCase() + newKey.substr(1);
  } else {
    newKey = newKey.substr(0, 1).toLowerCase() + newKey.substr(1);
  }

  if (!Object.prototype.hasOwnProperty.call(obj, newKey)) {
    // eslint-disable-next-line no-param-reassign
    obj[newKey] = obj[key];
    // eslint-disable-next-line no-param-reassign
    delete obj[key];
  }
}

function keyIterator(
  clonedObject: {[key: string]: unknown}, type: string, capitalize: boolean
): Record<string, unknown> {
  // Store a copy of the object before mutation
  const originalObject = { ...clonedObject };

  Object.keys(originalObject).forEach((key) => {
    // Do not convert headers for HTTP Request Blocks
    const isHTTPHeaders = [
      originalObject._type,
      originalObject.type
    ].includes("http_request_recipe") && key === "headers";

    if (isHTTPHeaders) {
      return;
    }

    convertKey(clonedObject, key, type, capitalize);

    // TODO:
    // When sending Normal Operating days to the API, we need to keep the weekday format in "Caps"
    // We should not have to do this. Cleanup task: (https://adasupport.atlassian.net/browse/FND-1438)
    if (key === "normalOperatingDays" || key === "normal_operating_days") {
      return;
    }

    // Perform operation again for nested objects
    if (typeof originalObject[key] === "object") {
      keyIterator(originalObject[key] as {[key: string]: unknown}, type, capitalize);
    }
  });

  return clonedObject;
}

export default function keyConverter(
  obj: Record<string, unknown>, type = "camel", capitalize = false
): {[key: string]: unknown} {
  if (!obj) {
    return null;
  }

  // Create a clone so that we do not mutate the original object
  let clonedObject;

  if (Array.isArray(obj)) {
    clonedObject = obj.slice(0);
  } else {
    clonedObject = JSON.parse(JSON.stringify(obj));
  }

  keyIterator(clonedObject, type, capitalize);

  return clonedObject;
}

/**
 * Prefix underscores to special keys (deep replace) an object in-place.
 * {"key":"snake_case","value":"1","type":"string"}
 */
export function addSpecialKeysToObj(obj: {[key: string]: unknown}): Record<string, unknown> {
  if (!obj) {
    return obj;
  }

  Object.entries(obj).forEach(([key, val]: [string, {[key: string]: unknown}]) => {
    const withUnderscore = `_${key}`;

    // Checks for user inserted "type" key and avoid prefixing with underscore.
    if (SPECIAL_KEYS.has(key) && !(key === "type" && typeof val !== "string")) {
      // TODO: Make this better so users can't manually insert special keys to break it
      // eslint-disable-next-line no-param-reassign
      obj[withUnderscore] = val;
      // eslint-disable-next-line no-param-reassign
      delete obj[key];
    }

    if (typeof val === "object") {
      addSpecialKeysToObj(val);
    }
  });

  return obj;
}

/**
 * Remove prefix on underscores on special keys (deep replace) an object in-place.
 *
 * {"key":"snake_case","value":"1","_type":"string"}
 */
export function removeSpecialKeysFromObj(
  obj: {[key: string]: unknown}
): { [key: string]: unknown } {
  if (!obj) {
    return obj;
  }

  const objCopy = cloneDeep(obj);
  Object.entries(objCopy).forEach(([key, val]: [string, {[key: string]: unknown}]) => {
    const keyWithoutLeadingUnderscore = key.replace(/^_/, "");
    let newVal = val;

    if (typeof val === "object") {
      newVal = removeSpecialKeysFromObj(val);
    }

    if (
      SPECIAL_KEYS.has(keyWithoutLeadingUnderscore) &&
      // allow users to enter keys like `id`
      keyWithoutLeadingUnderscore !== key && !(key === "_type" && typeof val !== "string")
    ) {
      delete objCopy[key];
      objCopy[keyWithoutLeadingUnderscore] = newVal;
    } else {
      objCopy[key] = newVal;
    }
  });

  return objCopy;
}
